// Trip and Vertical Constants for FusionCab - Backend Compatible

export const VERTICALS = {
  INTERCITY: "intercity",
  AIRPORT: "airport",
  HOURLY: "hourly",
} as const;

export const TRIP_TYPES = {
  ONEWAY: "one-way",
  ROUNDTRIP: "round-trip",
  MULTICITY: "multi-city",
  PICKUP: "pickup",
  DROPOFF: "drop-off",
  HRS4: "4hrs/40kms",
  HRS8: "8hrs/80kms",
  HRS12: "12hrs/120kms",
} as const;

// Duration mapping for hourly rentals
export const HOURLY_DURATION_MAPPING = {
  "4": "4hrs/40kms",
  "8": "8hrs/80kms",
  "12": "12hrs/120kms",
} as const;

// Type definitions for TypeScript
export type VerticalType = (typeof VERTICALS)[keyof typeof VERTICALS];
export type TripType = (typeof TRIP_TYPES)[keyof typeof TRIP_TYPES];

// Display options for the vertical tabs (Intercity / Airport / Hourly)
export const VERTICAL_OPTIONS: { value: VerticalType; label: string }[] = [
  { value: VERTICALS.INTERCITY, label: "Intercity" },
  { value: VERTICALS.AIRPORT, label: "Airport" },
  { value: VERTICALS.HOURLY, label: "Hourly rentals" },
];

// Trip-type sub-options, scoped per vertical, for data-driven tab rendering
export const TRIP_TYPES_BY_VERTICAL: Record<
  VerticalType,
  { value: TripType; label: string }[]
> = {
  [VERTICALS.INTERCITY]: [
    { value: TRIP_TYPES.ONEWAY, label: "One-Way" },
    { value: TRIP_TYPES.ROUNDTRIP, label: "Round Trip" },
    { value: TRIP_TYPES.MULTICITY, label: "Multi City" },
  ],
  [VERTICALS.AIRPORT]: [
    { value: TRIP_TYPES.PICKUP, label: "Pickup" },
    { value: TRIP_TYPES.DROPOFF, label: "Drop Off" },
  ],
  [VERTICALS.HOURLY]: [
    { value: TRIP_TYPES.HRS4, label: "4hrs/40kms" },
    { value: TRIP_TYPES.HRS8, label: "8hrs/80kms" },
    { value: TRIP_TYPES.HRS12, label: "12hrs/120kms" },
  ],
};

// Default trip type to select when switching to a given vertical
export const DEFAULT_TRIP_TYPE_BY_VERTICAL: Record<VerticalType, TripType> = {
  [VERTICALS.INTERCITY]: TRIP_TYPES.ONEWAY,
  [VERTICALS.AIRPORT]: TRIP_TYPES.PICKUP,
  [VERTICALS.HOURLY]: TRIP_TYPES.HRS4,
};

// Reverse lookup of HOURLY_DURATION_MAPPING - derives the "hours" search
// parameter directly from an already-selected hourly TripType value, since
// the hourly duration is chosen via the trip-type tabs, not a separate field.
export function getHourlyDurationKey(tripType: TripType): string | undefined {
  const entry = Object.entries(HOURLY_DURATION_MAPPING).find(
    ([, value]) => value === tripType
  );
  return entry?.[0];
}

// Shared field-visibility/validation rules for every search form (Landing,
// Dashboard, and their sub-components) so they can never drift apart again.
//
// For Airport, there is no separate "<City> Airport" location - "the
// airport" is just whichever ordinary City the customer picks (e.g.
// "Hyderabad"), matching how fusion_admindash's Plan form works: an Airport
// Plan is a single City on one side, with the other side left null. So both
// fields stay visible for Airport, same as One-Way - see
// isSourceCityOnly/isDestinationCityOnly below for which side must be a
// plain City pick (not a free Google-Places address).
export function isSourceFieldVisible(
  _vertical: VerticalType,
  _tripType: TripType
): boolean {
  return true;
}

export function isDestinationFieldVisible(
  vertical: VerticalType,
  _tripType: TripType
): boolean {
  return vertical !== VERTICALS.HOURLY;
}

// Airport Pickup: the customer is picked up AT the airport, so the source
// field must be a real City matching the Plan (e.g. "Hyderabad"), not a
// free-form address - the destination (drop-off) is a free address instead.
export function isSourceCityOnly(
  vertical: VerticalType,
  tripType: TripType
): boolean {
  return vertical === VERTICALS.AIRPORT && tripType === TRIP_TYPES.PICKUP;
}

// Airport Drop-off: the customer is dropped AT the airport, so the
// destination field must be a real City matching the Plan - the source
// (pickup) is a free address instead.
export function isDestinationCityOnly(
  vertical: VerticalType,
  tripType: TripType
): boolean {
  return vertical === VERTICALS.AIRPORT && tripType === TRIP_TYPES.DROPOFF;
}

// Round-Trip needs a return date because the car comes back to source on a
// trip the customer actually takes. Multi-City also needs it - the car
// still has to return to source after the final stop, billed into the same
// total fare, and the return date is what determines trip-day count for
// the per-day-km billing minimum (see fareCalculator's
// calculateMultiCityFare) - but that return leg is never shown to the
// customer as a route/itinerary step.
export function isReturnLegRequired(tripType: TripType): boolean {
  return tripType === TRIP_TYPES.ROUNDTRIP || tripType === TRIP_TYPES.MULTICITY;
}

// Unlike the return date, a return/drop *time* is only meaningful for
// Round-Trip - Multi-City doesn't collect any per-stop/return time (night
// charges are decided by pickupTime alone, same as every other trip type).
export function isReturnTimeRequired(tripType: TripType): boolean {
  return tripType === TRIP_TYPES.ROUNDTRIP;
}
